export type TextMark = {
  start: number;
  end: number;
  highlight?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
};

export type StylePatch = {
  highlight?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
};

export const HIGHLIGHT_IDS = ["yellow", "cream", "pink", "lime", "orange", "cyan"] as const;
export type HighlightId = (typeof HIGHLIGHT_IDS)[number];

const HIGHLIGHT_ALIASES: Record<string, HighlightId> = {
  yellow: "yellow",
  zolty: "yellow",
  żółty: "yellow",
  cream: "cream",
  krem: "cream",
  pink: "pink",
  roz: "pink",
  róż: "pink",
  rozowy: "pink",
  różowy: "pink",
  lime: "lime",
  green: "lime",
  zielony: "lime",
  limonka: "lime",
  orange: "orange",
  pomarancz: "orange",
  pomarańcz: "orange",
  cyan: "cyan",
  blue: "cyan",
  niebieski: "cyan",
  blekit: "cyan",
  błękit: "cyan",
};

export function normalizeHighlightId(value?: string | null): HighlightId | "" {
  const key = String(value || "").trim().toLowerCase();
  return HIGHLIGHT_ALIASES[key] || "";
}

function hasStyle(row: StylePatch): boolean {
  return Boolean(row.highlight || row.bold || row.italic || row.underline || row.color);
}

function sameStyle(a: StylePatch, b: StylePatch): boolean {
  return (
    (a.highlight || "") === (b.highlight || "") &&
    Boolean(a.bold) === Boolean(b.bold) &&
    Boolean(a.italic) === Boolean(b.italic) &&
    Boolean(a.underline) === Boolean(b.underline) &&
    (a.color || "") === (b.color || "")
  );
}

const MARK_START_JUNK = " \t\n\"'”»";

function repairQuoteSpan(text: string, start: number, end: number): [number, number] {
  if (!text || start >= end || start >= text.length) return [start, end];
  if (text[start] !== "”") return [start, end];
  const openAt = text.lastIndexOf("„", start - 1);
  if (openAt < 0) return [start, end];
  return [openAt, start + 1];
}

export function normalizeMarks(marks: TextMark[] | undefined, text: string): TextMark[] {
  const n = text.length;
  const out: TextMark[] = [];
  for (const row of marks || []) {
    let start = Math.max(0, Number(row.start || 0));
    let end = Math.min(n, Number(row.end || 0));
    [start, end] = repairQuoteSpan(text, start, end);
    if (text[start] !== "”") {
      while (start < end && MARK_START_JUNK.includes(text[start] || "")) start += 1;
    }
    if (end <= start) continue;
    const next: TextMark = { start, end };
    const hl = normalizeHighlightId(row.highlight);
    if (hl) next.highlight = hl;
    if (row.bold) next.bold = true;
    if (row.italic) next.italic = true;
    if (row.underline) next.underline = true;
    if (row.color && /^#[0-9a-fA-F]{6}$/.test(row.color)) next.color = row.color;
    if (hasStyle(next)) out.push(next);
  }
  out.sort((a, b) => a.start - b.start || a.end - b.end);
  return out;
}

function styleAt(marks: TextMark[], a: number, b: number): StylePatch {
  const style: StylePatch = {};
  for (const row of marks) {
    if (row.start <= a && row.end >= b) {
      if (row.highlight) style.highlight = row.highlight;
      if (row.bold) style.bold = true;
      if (row.italic) style.italic = true;
      if (row.underline) style.underline = true;
      if (row.color) style.color = row.color;
    }
  }
  return style;
}

export function applyStyleMark(
  marks: TextMark[] | undefined,
  start: number,
  end: number,
  patch: StylePatch,
  text: string,
): TextMark[] {
  const n = text.length;
  const a = Math.max(0, Math.min(n, Math.min(start, end)));
  const b = Math.max(0, Math.min(n, Math.max(start, end)));
  const current = normalizeMarks(marks, text);
  if (a === b) return current;
  const cuts = new Set<number>([0, n, a, b]);
  for (const row of current) {
    cuts.add(row.start);
    cuts.add(row.end);
  }
  const points = [...cuts].filter((p) => p >= 0 && p <= n).sort((x, y) => x - y);
  const next: TextMark[] = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const from = points[i];
    const to = points[i + 1];
    if (to <= from) continue;
    const style = { ...styleAt(current, from, to) };
    if (from >= a && to <= b) {
      if (patch.highlight !== undefined) {
        if (patch.highlight) style.highlight = patch.highlight;
        else delete style.highlight;
      }
      if (patch.bold !== undefined) {
        if (patch.bold) style.bold = true;
        else delete style.bold;
      }
      if (patch.italic !== undefined) {
        if (patch.italic) style.italic = true;
        else delete style.italic;
      }
      if (patch.underline !== undefined) {
        if (patch.underline) style.underline = true;
        else delete style.underline;
      }
      if (patch.color !== undefined) {
        if (patch.color) style.color = patch.color;
        else delete style.color;
      }
    }
    if (hasStyle(style)) next.push({ start: from, end: to, ...style });
  }
  const merged: TextMark[] = [];
  for (const row of next) {
    const prev = merged[merged.length - 1];
    if (prev && prev.end === row.start && sameStyle(prev, row)) {
      prev.end = row.end;
    } else {
      merged.push({ ...row });
    }
  }
  return merged;
}

export function rangeIsStyled(
  marks: TextMark[] | undefined,
  start: number,
  end: number,
  key: keyof StylePatch,
  text = "",
): boolean {
  const a = Math.min(start, end);
  const b = Math.max(start, end);
  if (b <= a) return false;
  const current = normalizeMarks(marks, text || " ".repeat(Math.max(b, 1)));
  return current.some((row) => {
    if (row.start > a || row.end < b) return false;
    if (key === "highlight") return Boolean(row.highlight);
    return Boolean(row[key]);
  });
}

export function invertStylePatch(
  marks: TextMark[] | undefined,
  start: number,
  end: number,
  patch: StylePatch,
  text: string,
): StylePatch {
  const next: StylePatch = { ...patch };
  if (patch.bold === true && rangeIsStyled(marks, start, end, "bold", text)) next.bold = false;
  if (patch.italic === true && rangeIsStyled(marks, start, end, "italic", text)) next.italic = false;
  if (patch.underline === true && rangeIsStyled(marks, start, end, "underline", text)) next.underline = false;
  if (patch.highlight && rangeIsStyled(marks, start, end, "highlight", text)) next.highlight = "";
  return next;
}

export function isClearStylePatch(patch: StylePatch): boolean {
  return (
    patch.highlight === "" &&
    patch.bold === false &&
    patch.italic === false &&
    patch.underline === false &&
    patch.color === ""
  );
}

export function dropHighlight(marks: TextMark[] | undefined, text: string, color?: string): TextMark[] {
  const current = normalizeMarks(marks, text);
  const next: TextMark[] = [];
  for (const row of current) {
    const copy = { ...row };
    if (!color || copy.highlight === color) delete copy.highlight;
    if (hasStyle(copy)) next.push(copy);
  }
  return next;
}

export function selectionOrAll(
  range: { start: number; end: number } | null | undefined,
  text: string,
): { start: number; end: number } | null {
  if (range && range.end > range.start) return { start: range.start, end: range.end };
  const n = (text || "").length;
  if (!n) return null;
  return { start: 0, end: n };
}

export function editorOffsets(root: HTMLElement | null): { start: number; end: number } | null {
  if (!root) return null;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;
  const start = nodeOffset(root, range.startContainer, range.startOffset);
  const end = nodeOffset(root, range.endContainer, range.endOffset);
  if (start === end) return { start, end };
  return { start: Math.min(start, end), end: Math.max(start, end) };
}

function nodeLength(node: Node): number {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent?.length || 0;
  if ((node as HTMLElement).tagName === "BR") return 1;
  let n = 0;
  node.childNodes.forEach((child) => {
    n += nodeLength(child);
  });
  return n;
}

function nodeOffset(root: Node, target: Node, offset: number): number {
  if (target === root) {
    let n = 0;
    for (let i = 0; i < Math.min(offset, root.childNodes.length); i += 1) {
      n += nodeLength(root.childNodes[i]);
    }
    return n;
  }
  let n = 0;
  const walk = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let node = walk.nextNode();
  while (node) {
    if (node === target) {
      if (node.nodeType === Node.TEXT_NODE) return n + offset;
      let extra = 0;
      for (let i = 0; i < offset && i < node.childNodes.length; i += 1) {
        extra += nodeLength(node.childNodes[i]);
      }
      return n + extra;
    }
    if (node.nodeType === Node.TEXT_NODE) n += node.textContent?.length || 0;
    else if ((node as HTMLElement).tagName === "BR") n += 1;
    node = walk.nextNode();
  }
  return n;
}

function locateOffset(root: HTMLElement, pos: number): { node: Node; offset: number } {
  const target = Math.max(0, pos);
  let n = 0;
  const walk = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let node = walk.nextNode();
  let lastText: Text | null = null;
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node as Text;
      const len = text.data.length;
      if (n + len >= target) return { node: text, offset: target - n };
      n += len;
      lastText = text;
    } else if ((node as HTMLElement).tagName === "BR") {
      if (target <= n) {
        const parent = node.parentNode || root;
        const idx = Array.from(parent.childNodes).indexOf(node as ChildNode);
        return { node: parent, offset: Math.max(0, idx) };
      }
      n += 1;
      if (target === n) {
        const parent = node.parentNode || root;
        const idx = Array.from(parent.childNodes).indexOf(node as ChildNode) + 1;
        return { node: parent, offset: idx };
      }
    }
    node = walk.nextNode();
  }
  if (lastText) return { node: lastText, offset: lastText.data.length };
  return { node: root, offset: root.childNodes.length };
}

export function setEditorOffsets(root: HTMLElement | null, start: number, end: number) {
  if (!root) return;
  const sel = window.getSelection();
  if (!sel) return;
  const a = locateOffset(root, start);
  const b = locateOffset(root, end);
  const range = document.createRange();
  range.setStart(a.node, a.offset);
  range.setEnd(b.node, b.offset);
  sel.removeAllRanges();
  sel.addRange(range);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function marksToHtml(text: string, marks: TextMark[] | undefined): string {
  const rows = normalizeMarks(marks, text);
  if (!text) return "";
  const cuts = new Set<number>([0, text.length]);
  for (const row of rows) {
    cuts.add(row.start);
    cuts.add(row.end);
  }
  const points = [...cuts].sort((a, b) => a - b);
  return points
    .slice(0, -1)
    .map((a, i) => {
      const b = points[i + 1];
      const chunk = text.slice(a, b);
      const style = styleAt(rows, a, b);
      const css: string[] = [];
      if (style.highlight) {
        const fill = highlightCss(style.highlight);
        css.push(
          `background-image:linear-gradient(${fill},${fill})`,
          "background-size:100% 0.92em",
          "background-repeat:no-repeat",
          "background-position:0 0.08em",
          "box-decoration-break:clone",
          "-webkit-box-decoration-break:clone",
        );
      }
      if (style.bold) css.push("font-weight:700");
      if (style.italic) css.push("font-style:italic");
      if (style.underline) css.push("text-decoration:underline");
      if (style.color) css.push(`color:${style.color}`);
      const safe = escapeHtml(chunk).replace(/\n/g, "<br>");
      if (!hasStyle(style)) return safe;
      const data = [
        style.highlight ? `data-hl="${style.highlight}"` : "",
        style.bold ? 'data-bold="1"' : "",
        style.italic ? 'data-italic="1"' : "",
        style.underline ? 'data-underline="1"' : "",
        style.color ? `data-color="${style.color}"` : "",
      ]
        .filter(Boolean)
        .join(" ");
      return `<span ${data} style="${css.join(";")}">${safe}</span>`;
    })
    .join("");
}

export function htmlToMarks(root: HTMLElement): { text: string; marks: TextMark[] } {
  let text = "";
  const marks: TextMark[] = [];
  const walk = (node: Node, inherited: StylePatch) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const chunk = node.textContent || "";
      const start = text.length;
      text += chunk;
      const end = text.length;
      if (end > start && hasStyle(inherited)) marks.push({ start, end, ...inherited });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      node.childNodes.forEach((child) => walk(child, inherited));
      return;
    }
    const el = node as HTMLElement;
    if (el.tagName === "BR") {
      text += "\n";
      return;
    }
    const next: StylePatch = { ...inherited };
    const hl = normalizeHighlightId(el.getAttribute("data-hl") || "");
    if (hl) next.highlight = hl;
    if (el.getAttribute("data-bold") === "1" || el.tagName === "B" || el.tagName === "STRONG") next.bold = true;
    if (el.getAttribute("data-italic") === "1" || el.tagName === "I" || el.tagName === "EM") next.italic = true;
    if (el.getAttribute("data-underline") === "1" || el.tagName === "U") next.underline = true;
    const color = el.getAttribute("data-color") || "";
    if (color) next.color = color;
    const kids = Array.from(el.childNodes);
    if ((el.tagName === "DIV" || el.tagName === "P") && text && !text.endsWith("\n")) text += "\n";
    kids.forEach((child) => walk(child, next));
  };
  walk(root, {});
  return { text: text.replace(/\r/g, ""), marks: normalizeMarks(marks, text) };
}

export function findTextEdit(oldText: string, newText: string): { a: number; oldB: number; newB: number } {
  const oldS = oldText || "";
  const newS = newText || "";
  let a = 0;
  const limit = Math.min(oldS.length, newS.length);
  while (a < limit && oldS[a] === newS[a]) a += 1;
  let oldB = oldS.length;
  let newB = newS.length;
  while (oldB > a && newB > a && oldS[oldB - 1] === newS[newB - 1]) {
    oldB -= 1;
    newB -= 1;
  }
  return { a, oldB, newB };
}

function mapOffset(pos: number, a: number, oldB: number, newB: number, bias: "start" | "end"): number {
  if (pos < a) return pos;
  if (pos > oldB) return pos + (newB - oldB);
  if (pos === oldB) return bias === "start" && pos === a ? a : newB;
  return bias === "start" ? a : newB;
}

export function remapOffsetOnEdit(pos: number, oldText: string, newText: string, bias: "start" | "end" = "end"): number {
  if ((oldText || "") === (newText || "")) return pos;
  const { a, oldB, newB } = findTextEdit(oldText || "", newText || "");
  return mapOffset(pos, a, oldB, newB, bias);
}

export function remapMarksOnEdit(oldText: string, newText: string, marks: TextMark[] | undefined): TextMark[] {
  const current = normalizeMarks(marks, oldText || "");
  if (!current.length) return [];
  if ((oldText || "") === (newText || "")) return normalizeMarks(current, newText || "");
  const { a, oldB, newB } = findTextEdit(oldText || "", newText || "");
  const next: TextMark[] = [];
  for (const row of current) {
    const start = mapOffset(row.start, a, oldB, newB, "start");
    const end = mapOffset(row.end, a, oldB, newB, "end");
    if (end <= start) continue;
    next.push({ ...row, start, end });
  }
  return normalizeMarks(next, newText || "");
}

export function editorPlainText(root: HTMLElement): string {
  return htmlToMarks(root).text;
}

function isWordChar(ch: string | undefined): boolean {
  if (!ch) return false;
  return /[0-9A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż]/.test(ch);
}

function markOnWordBoundary(text: string, start: number, end: number): boolean {
  if (!(start >= 0 && start < end && end <= text.length)) return false;
  if (start > 0 && isWordChar(text[start - 1]) && isWordChar(text[start])) return false;
  if (end < text.length && isWordChar(text[end - 1]) && isWordChar(text[end])) return false;
  return true;
}

export function emitInlineMarkdown(text: string, marks?: TextMark[]): string {
  const src = text || "";
  if (!src) return "";
  const spans: { start: number; end: number; bold: boolean }[] = [];
  for (const row of marks || []) {
    const start = Number(row.start || 0);
    const end = Number(row.end || 0);
    if (!(start >= 0 && start < end && end <= src.length)) continue;
    if (!markOnWordBoundary(src, start, end)) continue;
    const bold = Boolean(row.bold || row.highlight);
    const italic = Boolean(row.italic);
    if (!bold && !italic) continue;
    spans.push({ start, end, bold });
  }
  if (!spans.length) return src;
  const merged: { start: number; end: number; bold: boolean }[] = [];
  for (const row of [...spans].sort((a, b) => a.start - b.start || b.end - a.end || Number(b.bold) - Number(a.bold))) {
    const last = merged[merged.length - 1];
    if (last && last.start === row.start && last.end === row.end) {
      last.bold = last.bold || row.bold;
      continue;
    }
    merged.push({ ...row });
  }
  let out = src;
  for (const row of [...merged].sort((a, b) => b.start - a.start)) {
    const inner = out.slice(row.start, row.end);
    if (inner.startsWith("**") || (!row.bold && inner.startsWith("*") && inner.endsWith("*"))) continue;
    const wrapped = row.bold ? `**${inner}**` : `*${inner}*`;
    out = `${out.slice(0, row.start)}${wrapped}${out.slice(row.end)}`;
  }
  return out;
}

export function highlightCss(value?: string) {
  const key = normalizeHighlightId(value);
  if (key === "yellow") return "rgba(255, 224, 138, 0.85)";
  if (key === "cream") return "rgba(244, 229, 196, 0.9)";
  if (key === "pink") return "rgba(255, 121, 198, 0.78)";
  if (key === "lime") return "rgba(163, 255, 71, 0.78)";
  if (key === "orange") return "rgba(255, 171, 64, 0.8)";
  if (key === "cyan") return "rgba(92, 225, 255, 0.78)";
  return "transparent";
}

/** Neonowy swatch przycisku markera (nieprzezroczysty, jak flamastr). */
export function highlightSwatchCss(value?: string): string {
  const key = normalizeHighlightId(value);
  if (key === "yellow") return "rgb(255, 224, 138)";
  if (key === "cream") return "rgb(244, 229, 196)";
  if (key === "pink") return "rgb(255, 121, 198)";
  if (key === "lime") return "rgb(163, 255, 71)";
  if (key === "orange") return "rgb(255, 171, 64)";
  if (key === "cyan") return "rgb(92, 225, 255)";
  return "transparent";
}

/** Marker jak na PNG: pas ~1em przy literach, nie cały line-height boxa. */
export function highlightMarkerStyle(value?: string): Record<string, string> {
  const fill = highlightCss(value);
  if (fill === "transparent") return {};
  return {
    backgroundImage: `linear-gradient(${fill}, ${fill})`,
    backgroundSize: "100% 0.92em",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "0 0.08em",
    boxDecorationBreak: "clone",
    WebkitBoxDecorationBreak: "clone",
  };
}
